import { ReaderInterface } from '@am3nd-org/pointcraft-stripe-terminal';
import { UserDataType, ProductDataType } from '@am3nd-org/pointcraft-types';

import { chunkArray } from 'src/utils/chunk-array';

import { DeviceType } from 'src/store/SystemStore/types';
import type { SavedPrinter, SavedStarPrinter } from 'src/services/printers/star-print.types';

import { CompanionDeviceType } from 'src/types/companion';

import PlatformService from './PlatformDBService';
import { isStripeM2ReaderSessionMetadataKey } from './stripe-m2-reader-session-storage';
import { type DiagnosticAdmission, assertDiagnosticAdmission } from './diagnostic-admission';
import {
  isStripeM2OfflineForwardingRecordKey,
  STRIPE_M2_OFFLINE_FORWARDING_RECORD_PREFIX,
} from './stripe-m2-offline-forwarding-storage';
import type {
  InventoryStats,
  SystemConfigType,
  DiagnosticLogRecordRow,
  DiagnosticLogPageQuery,
  DiagnosticLogStorageStats,
  NewDiagnosticLogRecordRow,
  RestaurantOrderEnrichmentCacheRecord,
} from './types';

export type {
  InventoryStats,
  DiagnosticLogRecordRow,
  DiagnosticLogStorageStats,
  NewDiagnosticLogRecordRow,
  RestaurantOrderEnrichmentCacheRecord,
};
export { STRIPE_M2_OFFLINE_FORWARDING_RECORD_PREFIX };

// Prevents a Firestore sync from writing to the inventory table while a
// one-time chunk migration is still in progress.
let isMigrating = false;

// Manager Tap to Pay setup history is durable device-local configuration, not
// a session cache. Each record has its own business/user/provider-scoped key so
// unrelated system_config writers can never replace it.
export const MANAGER_TAP_TO_PAY_SETUP_DB_PREFIX = 'pointcraft.manager.tapToPaySetup.v3';

function isManagerTapToPaySetupKey(type: string): boolean {
  return type.startsWith(`${MANAGER_TAP_TO_PAY_SETUP_DB_PREFIX}:`);
}

function assertManagerTapToPaySetupKey(type: string): void {
  if (!isManagerTapToPaySetupKey(type)) {
    throw new Error('Invalid Manager Tap to Pay setup database key');
  }
}

export const getManagerTapToPaySetupRecordFromDB = async <T>(key: string): Promise<T | null> => {
  assertManagerTapToPaySetupKey(key);
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<T>(key);
  return record?.data ?? null;
};

export const saveManagerTapToPaySetupRecordToDB = async <T>(
  key: string,
  record: T
): Promise<void> => {
  assertManagerTapToPaySetupKey(key);
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(key, record);
  // Some platform adapters return after a failed write. Approval is durable
  // only after read-back confirms the scoped record, as with active sale drafts.
  const persisted = await instance.getSystemConfig<T>(key);
  if (!persisted || JSON.stringify(persisted.data) !== JSON.stringify(record)) {
    throw new Error('Tap to Pay setup database verification failed');
  }
};

export const deleteStripeTerminalConfig = async () => {
  const instance = await PlatformService.getInstance();
  await instance.deleteSystemConfig('connectedStripeTerminal');
};

export const getStripeTerminalConfigSaved = async () => {
  const instance = await PlatformService.getInstance();
  const reader = await instance.getSystemConfig<{
    readerData: ReaderInterface;
    readerId: string;
  }>('connectedStripeTerminal');
  return reader;
};

// ---------------------------------------------------------------------------
// Force logout control state — tracks which logout commands this device has
// already handled, preventing infinite logout loops after re-login.
// ---------------------------------------------------------------------------
const FORCE_LOGOUT_CONTROL_STATE_KEY = 'forceLogoutControlState';

export type ForceLogoutControlState = {
  businessId: string;
  deviceIdOrUserId: string;
  version: number;
  initializedAt: string; // ISO 8601
  shownAt?: string;
  handledAt?: string;
  status: 'initialized' | 'shown' | 'logged_out';
};

export const getForceLogoutControlState = async (): Promise<ForceLogoutControlState | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<ForceLogoutControlState>(
    FORCE_LOGOUT_CONTROL_STATE_KEY
  );
  return record?.data ?? null;
};

export const saveForceLogoutControlState = async (
  state: ForceLogoutControlState
): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(FORCE_LOGOUT_CONTROL_STATE_KEY, state);
};

export const clearForceLogoutControlState = async (): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.deleteSystemConfig(FORCE_LOGOUT_CONTROL_STATE_KEY);
};

const LEGACY_CURRENT_DEVICE_KEY = 'currentDevice';

export type LegacyPosCurrentDeviceTypeInDb = {
  id: string | null; // Allow null for companion devices without business location ID
  name: string;
  type: DeviceType;
  initialized: boolean;
  capacitorDeviceId: string | null;
  companionType?: CompanionDeviceType; // Track specific companion device type
};

export const getLegacyPosCurrentDeviceFromDB =
  async (): Promise<SystemConfigType<LegacyPosCurrentDeviceTypeInDb> | null> => {
    const instance = await PlatformService.getInstance();
    const device =
      await instance.getSystemConfig<LegacyPosCurrentDeviceTypeInDb>(LEGACY_CURRENT_DEVICE_KEY);

    return device?.data?.companionType ? null : device;
  };

export const deleteLegacyPosCurrentDeviceFromDB = async (): Promise<void> => {
  const instance = await PlatformService.getInstance();
  const device =
    await instance.getSystemConfig<LegacyPosCurrentDeviceTypeInDb>(LEGACY_CURRENT_DEVICE_KEY);

  if (!device || device.data.companionType) return;
  await instance.deleteSystemConfig(LEGACY_CURRENT_DEVICE_KEY);
};

export type CompanionCurrentDeviceTypeInDb = LegacyPosCurrentDeviceTypeInDb;

const COMPANION_CURRENT_DEVICE_KEY = 'companionCurrentDevice';

export const setCompanionCurrentDeviceInDB = async (device: CompanionCurrentDeviceTypeInDb) => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(COMPANION_CURRENT_DEVICE_KEY, device);
};

export const getCompanionCurrentDeviceFromDB =
  async (): Promise<SystemConfigType<CompanionCurrentDeviceTypeInDb> | null> => {
    const instance = await PlatformService.getInstance();
    const device = await instance.getSystemConfig<CompanionCurrentDeviceTypeInDb>(
      COMPANION_CURRENT_DEVICE_KEY
    );
    if (device) return device;

    const legacy =
      await instance.getSystemConfig<CompanionCurrentDeviceTypeInDb>(LEGACY_CURRENT_DEVICE_KEY);
    if (!legacy?.data?.companionType) return null;

    await instance.updateOrCreateSystemConfig(COMPANION_CURRENT_DEVICE_KEY, legacy.data);
    await instance.deleteSystemConfig(LEGACY_CURRENT_DEVICE_KEY);
    return legacy;
  };

export type CompanionDeviceTypeInDb = {
  deviceId: string;
  code: string;
  timestamp: number;
  companionType?: CompanionDeviceType; // The specific type of companion device
};
export const getCompanionDevice = async () => {
  const instance = await PlatformService.getInstance();
  const device = await instance.getSystemConfig<CompanionDeviceTypeInDb>('companionDevice');
  return device;
};

export const saveCompanionDevice = async (device: CompanionDeviceTypeInDb) => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig('companionDevice', device);
};

export const clearLocalDataForLogout = async () => {
  const instance = await PlatformService.getInstance();

  const forceLogoutState = await getForceLogoutControlState();
  // Preserve records in place. A delete-all followed by restore loses financial
  // evidence if the process exits between the two writes.
  const cleanupSystemConfigs = async () => {
    let records: SystemConfigType<unknown>[];
    try {
      records = await instance.getAllSystemConfigs<unknown>();
    } catch {
      return;
    } // Unknown contents must be retained for the next signed-in recovery.
    await Promise.all(
      records
        .filter(
          (record) =>
            !record.type.startsWith('payment_retention_v1:') &&
            !isManagerTapToPaySetupKey(record.type) &&
            !isStripeM2OfflineForwardingRecordKey(record.type) &&
            !isStripeM2ReaderSessionMetadataKey(record.type) &&
            !record.type.toLowerCase().includes('forcelogout')
        )
        .map((record) => instance.deleteSystemConfig(record.type))
    );
  };

  // `diagnostic_log_records` is deliberately NOT cleared: a logout loop is
  // frequently the thing being diagnosed. Rows carry their own businessId, the
  // uploader only ever claims rows for the current business, and the ingest
  // endpoint derives the tenant from the device principal, so a re-paired
  // device can never ship another tenant's logs. Anything left over expires
  // through the 24-hour retention pass. See src/features/diagnostics.
  await Promise.all([
    cleanupSystemConfigs(),
    instance.clearAdminBusinesses(),
    instance.clearAllInventory(),
    instance.clearRestaurantOrderEnrichmentCache(),
  ]);

  // Refresh the derived inventory cache after its source table is emptied.
  await instance.recalculateInventoryStats();

  // Re-persist force logout state as 'logged_out' so the next login can check the version
  // and avoid re-triggering the same logout command.
  if (forceLogoutState) {
    await saveForceLogoutControlState({
      ...forceLogoutState,
      status: 'logged_out',
      handledAt: forceLogoutState.handledAt ?? new Date().toISOString(),
    });
  }
};

// POS ACTIVE TAB
export type PosActiveTabInDb = {
  value: '0' | '1';
};
export const getPosActiveTab = async (): Promise<SystemConfigType<PosActiveTabInDb> | null> => {
  const instance = await PlatformService.getInstance();
  const tab = await instance.getSystemConfig<PosActiveTabInDb>('posActiveTab');
  return tab;
};

export const savePosActiveTab = async (tab: PosActiveTabInDb): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig('posActiveTab', tab);
};

// save chosen ble reader to db
export const saveChosenBleReaderToDB = async (reader: { serialNumber: string }): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig('chosenBleReader', reader);
};

// get chosen ble reader from db
export const getChosenBleReaderFromDB = async (): Promise<{ serialNumber: string } | null> => {
  const instance = await PlatformService.getInstance();
  const reader = await instance.getSystemConfig<{ serialNumber: string }>('chosenBleReader');
  return reader?.data || null;
};

// delete chosen ble reader from db
export const deleteChosenBleReaderFromDB = async (): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.deleteSystemConfig('chosenBleReader');
};

// ---------------------------------------------------------------------------
// Default Star printer — selected via Settings ▸ System ▸ Printers and used by
// the new StarPrint Capacitor plugin to reconnect headlessly. The full
// connection object is stored so LAN ip/port, BT MAC and USB port survive.
// ---------------------------------------------------------------------------
const DEFAULT_STAR_PRINTER_KEY = 'defaultStarPrinter';

export const saveDefaultStarPrinterToDB = async (printer: SavedStarPrinter): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(DEFAULT_STAR_PRINTER_KEY, printer);
};

export const getDefaultStarPrinterFromDB = async (): Promise<SavedStarPrinter | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<SavedStarPrinter>(DEFAULT_STAR_PRINTER_KEY);
  return record?.data ?? null;
};

export const deleteDefaultStarPrinterFromDB = async (): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.deleteSystemConfig(DEFAULT_STAR_PRINTER_KEY);
};

// ---------------------------------------------------------------------------
// Star printers (v2) — the single active printer plus the list of remembered
// printers, owned by the central printing service / PrinterStore. Supersedes
// the single `defaultStarPrinter` key above (migrated on first load). The active
// printer is ALSO mirrored to `defaultStarPrinter` via `saveDefaultStarPrinterToDB`
// so `starPrintService`'s headless reconnect keeps working unchanged.
// ---------------------------------------------------------------------------
const STAR_PRINTERS_KEY = 'star_printers_v2';

export interface StarPrintersState {
  activeId: string | null;
  printers: SavedPrinter[];
  /**
   * The printing method chosen in Settings (`'star'` | `'escpos'`). Absent on
   * records written before ESC/POS existed, which means Star.
   */
  method?: 'star' | 'escpos';
}

export const saveStarPrintersToDB = async (state: StarPrintersState): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(STAR_PRINTERS_KEY, state);
};

export const getStarPrintersFromDB = async (): Promise<StarPrintersState | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<StarPrintersState>(STAR_PRINTERS_KEY);
  return record?.data ?? null;
};

// ---------------------------------------------------------------------------
// Inventory sync metadata — proves which tenant owns the local inventory mirror.
// ---------------------------------------------------------------------------
const INVENTORY_SYNC_META_KEY = 'inventory_sync_meta';
export const INVENTORY_CACHE_SCHEMA_VERSION = 2;

export type InventorySyncMeta = {
  version: number;
  lastSyncedAt: string; // ISO 8601
  businessId: string;
  schemaVersion: number;
  recordCount: number;
};

export const getInventorySyncMeta = async (): Promise<InventorySyncMeta | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<InventorySyncMeta>(INVENTORY_SYNC_META_KEY);
  return record?.data ?? null;
};

export const saveInventorySyncMeta = async (meta: InventorySyncMeta): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(INVENTORY_SYNC_META_KEY, meta);
};

// ---------------------------------------------------------------------------
// User data cache — full UserDataType blob so the store can be hydrated
// immediately on app boot without waiting for the preload network request.
// ---------------------------------------------------------------------------
const USER_DATA_CACHE_KEY = 'userData_cache';

export const getUserDataCache = async (): Promise<UserDataType | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<UserDataType>(USER_DATA_CACHE_KEY);
  return record?.data ?? null;
};

export const saveUserDataCache = async (data: UserDataType): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(USER_DATA_CACHE_KEY, data);
};

// ---------------------------------------------------------------------------
// Manager app API cache — small, scoped response snapshots stored in
// system_config so Manager can render stale/offline data while fresh data loads.
// ---------------------------------------------------------------------------
export type ManagerApiCacheEntry<T> = {
  cacheKey: string;
  data: T;
  fetchedAt: string; // ISO 8601
  maxUpdatedAt?: string | null;
};

export const getManagerApiCacheEntry = async <T>(
  cacheKey: string
): Promise<ManagerApiCacheEntry<T> | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<ManagerApiCacheEntry<T>>(cacheKey);
  return record?.data ?? null;
};

export const saveManagerApiCacheEntry = async <T>(
  entry: ManagerApiCacheEntry<T>
): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(entry.cacheKey, entry);
};

// ---------------------------------------------------------------------------
// Restaurant Orders enrichment cache — dedicated, tenant-scoped table/store.
// ---------------------------------------------------------------------------

const restaurantOrderCacheKey = (businessId: string, saleId: string) => `${businessId}:${saleId}`;

export namespace RestaurantOrderEnrichmentLocalDB {
  export async function get<T>(
    businessId: string,
    saleId: string
  ): Promise<RestaurantOrderEnrichmentCacheRecord<T> | null> {
    const instance = await PlatformService.getInstance();
    const record = await instance.getRestaurantOrderEnrichmentCache(businessId, saleId);
    return record as RestaurantOrderEnrichmentCacheRecord<T> | null;
  }

  export async function upsert<T>(
    record: Omit<RestaurantOrderEnrichmentCacheRecord<T>, 'cacheKey'>
  ): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.upsertRestaurantOrderEnrichmentCache({
      ...record,
      cacheKey: restaurantOrderCacheKey(record.businessId, record.saleId),
    });
  }

  export async function deleteExpired(before = new Date()): Promise<number> {
    const instance = await PlatformService.getInstance();
    return instance.deleteExpiredRestaurantOrderEnrichmentCache(before.toISOString());
  }

  export async function clearAll(): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.clearRestaurantOrderEnrichmentCache();
  }
}

// ---------------------------------------------------------------------------
// Diagnostic log records — device diagnostic logging (src/features/diagnostics)
// ---------------------------------------------------------------------------

export namespace DiagnosticLogLocalDB {
  export async function readPage(
    query: DiagnosticLogPageQuery,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    assertDiagnosticAdmission(admit);
    const instance = await PlatformService.getForDiagnostics(admit);
    assertDiagnosticAdmission(admit);
    return instance.readDiagnosticLogPage(query, admit);
  }

  /** One coalesced write per batch. Never called per record. */
  export async function insertMany(
    records: readonly NewDiagnosticLogRecordRow[],
    admit: DiagnosticAdmission
  ): Promise<void> {
    if (records.length === 0) return;
    assertDiagnosticAdmission(admit);
    const instance = await PlatformService.getForDiagnostics(admit);
    assertDiagnosticAdmission(admit);
    await instance.insertDiagnosticLogRecords(records, admit);
  }

  /** Oldest-first, bounded by both a record count and a serialized byte budget. */
  export async function claim(
    businessId: string,
    limit: number,
    maxBytes: number,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    assertDiagnosticAdmission(admit);
    const instance = await PlatformService.getForDiagnostics(admit);
    assertDiagnosticAdmission(admit);
    return instance.claimDiagnosticLogRecords(businessId, limit, maxBytes, admit);
  }

  export async function remove(
    recordIds: readonly string[],
    admit: DiagnosticAdmission
  ): Promise<void> {
    if (recordIds.length === 0) return;
    assertDiagnosticAdmission(admit);
    const instance = await PlatformService.getForDiagnostics(admit);
    assertDiagnosticAdmission(admit);
    await instance.deleteDiagnosticLogRecords(recordIds, admit);
  }

  export async function deleteOlderThan(
    occurredBefore: Date,
    admit: DiagnosticAdmission
  ): Promise<number> {
    assertDiagnosticAdmission(admit);
    const instance = await PlatformService.getForDiagnostics(admit);
    assertDiagnosticAdmission(admit);
    return instance.deleteDiagnosticLogRecordsOlderThan(occurredBefore.toISOString(), admit);
  }

  /** Tenant hygiene after a device is re-paired to a different business. */
  export async function deleteForeignBusinesses(
    businessId: string,
    admit: DiagnosticAdmission
  ): Promise<number> {
    assertDiagnosticAdmission(admit);
    const instance = await PlatformService.getForDiagnostics(admit);
    assertDiagnosticAdmission(admit);
    return instance.deleteDiagnosticLogRecordsForOtherBusinesses(businessId, admit);
  }

  export async function trimToBytes(maxBytes: number, admit: DiagnosticAdmission): Promise<number> {
    assertDiagnosticAdmission(admit);
    const instance = await PlatformService.getForDiagnostics(admit);
    assertDiagnosticAdmission(admit);
    return instance.trimDiagnosticLogRecordsToBytes(maxBytes, admit);
  }

  export async function stats(admit: DiagnosticAdmission): Promise<DiagnosticLogStorageStats> {
    assertDiagnosticAdmission(admit);
    const instance = await PlatformService.getForDiagnostics(admit);
    assertDiagnosticAdmission(admit);
    return instance.getDiagnosticLogStorageStats(admit);
  }

  export async function clearAll(admit: DiagnosticAdmission): Promise<void> {
    assertDiagnosticAdmission(admit);
    const instance = await PlatformService.getForDiagnostics(admit);
    assertDiagnosticAdmission(admit);
    await instance.clearDiagnosticLogRecords(admit);
  }
}

// ---------------------------------------------------------------------------
// Inventory cache — chunked to handle large product catalogs (6k+ records)
// ---------------------------------------------------------------------------
const INVENTORY_META_KEY = 'cachedInventory_meta';
const inventoryChunkKey = (i: number) => `cachedInventory_chunk_${i}`;
const INVENTORY_CHUNK_SIZE = 500;

export const saveInventoryToLocalDb = async (products: ProductDataType[]): Promise<void> => {
  const instance = await PlatformService.getInstance();
  const chunks = chunkArray(products, INVENTORY_CHUNK_SIZE);
  await Promise.all([
    instance.updateOrCreateSystemConfig(INVENTORY_META_KEY, { chunkCount: chunks.length }),
    ...chunks.map((chunk, i) => instance.updateOrCreateSystemConfig(inventoryChunkKey(i), chunk)),
  ]);
};

export const getInventoryFromLocalDb = async (): Promise<ProductDataType[] | null> => {
  const instance = await PlatformService.getInstance();
  const meta = await instance.getSystemConfig<{ chunkCount: number }>(INVENTORY_META_KEY);
  if (!meta?.data?.chunkCount) return null;

  const chunkResults = await Promise.all(
    Array.from({ length: meta.data.chunkCount }, (_, i) =>
      instance.getSystemConfig<ProductDataType[]>(inventoryChunkKey(i))
    )
  );

  // Treat missing chunks as a corrupt cache
  if (chunkResults.some((r) => !r?.data)) return null;
  return chunkResults.flatMap((r) => r!.data);
};

export const clearInventoryFromLocalDb = async (): Promise<void> => {
  const instance = await PlatformService.getInstance();
  const meta = await instance.getSystemConfig<{ chunkCount: number }>(INVENTORY_META_KEY);
  const chunkCount = meta?.data?.chunkCount ?? 0;
  await Promise.all([
    instance.deleteSystemConfig(INVENTORY_META_KEY),
    ...Array.from({ length: chunkCount }, (_, i) =>
      instance.deleteSystemConfig(inventoryChunkKey(i))
    ),
  ]);
};

// ---------------------------------------------------------------------------
// Held Sales Queue — stores held sales in the system_config table with 72h TTL
// ---------------------------------------------------------------------------
const HELD_SALES_QUEUE_KEY = 'held_sales_queue';

export type HeldSaleItem = {
  id: string;
  heldAt: string; // ISO 8601
  expiresAt: string; // ISO 8601 — heldAt + 72h
  sale: any; // SaleData — typed as any here to avoid circular dep with @am3nd-org/pointcraft-types
};

export const getHeldSalesQueueFromDB = async (): Promise<HeldSaleItem[]> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<HeldSaleItem[]>(HELD_SALES_QUEUE_KEY);
  return record?.data ?? [];
};

export const saveHeldSalesQueueToDB = async (queue: HeldSaleItem[]): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(HELD_SALES_QUEUE_KEY, queue);
};

// ---------------------------------------------------------------------------
// Active POS sale draft — one versioned, register-scoped system_config record
// ---------------------------------------------------------------------------
export const ACTIVE_SALE_DRAFT_DB_PREFIX = 'pointcraft.pos.activeSale.v1:';

function assertActiveSaleDraftKey(key: string): void {
  if (!key.startsWith(ACTIVE_SALE_DRAFT_DB_PREFIX)) {
    throw new Error('Invalid active sale draft database key');
  }
}

export const getActiveSaleDraftRecordFromDB = async <T>(key: string): Promise<T | null> => {
  assertActiveSaleDraftKey(key);
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<T>(key);
  return record?.data ?? null;
};

export const saveActiveSaleDraftRecordToDB = async <T>(key: string, record: T): Promise<void> => {
  assertActiveSaleDraftKey(key);
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(key, record);
  // SQLite/Dexie retain historical catch-and-return behavior for some failures.
  // Verify this privacy-sensitive write so the active-sale fallback can surface
  // a database failure instead of assuming the mirror was durably repaired.
  const persisted = await instance.getSystemConfig<T>(key);
  if (!persisted || JSON.stringify(persisted.data) !== JSON.stringify(record)) {
    throw new Error('Active sale draft database verification failed');
  }
};

// ---------------------------------------------------------------------------
// InventoryLocalDB — helpers for the dedicated inventory table.
// These replace the chunked system_config approach for all new code paths.
// ---------------------------------------------------------------------------
const MIGRATION_SENTINEL_KEY = 'inventory_table_migrated_v1';
type InventoryChangeListener = () => void;
const inventoryChangeListeners = new Set<InventoryChangeListener>();

function notifyInventoryChanged(): void {
  inventoryChangeListeners.forEach((listener) => listener());
}

export namespace InventoryLocalDB {
  /** Subscribe to completed inventory-table writes. */
  export function subscribe(listener: InventoryChangeListener): () => void {
    inventoryChangeListeners.add(listener);
    return () => inventoryChangeListeners.delete(listener);
  }

  /** Upsert the full product list (used by full sync). */
  export async function saveAll(products: ProductDataType[]): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.bulkUpsertInventory(products);
    notifyInventoryChanged();
    await instance
      .recalculateInventoryStats()
      .catch((err) =>
        console.error('[InventoryLocalDB] Stats recalculation failed after saveAll:', err)
      );
  }

  /** Upsert a subset of products (used by incremental sync). */
  export async function upsertMany(products: ProductDataType[]): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.bulkUpsertInventory(products);
    notifyInventoryChanged();
    await instance
      .recalculateInventoryStats()
      .catch((err) =>
        console.error('[InventoryLocalDB] Stats recalculation failed after upsertMany:', err)
      );
  }

  /** Upsert a single product (used by Add/Edit dialogs for immediate consistency). */
  export async function upsertOne(product: ProductDataType): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.upsertInventoryItem(product);
    notifyInventoryChanged();
    await instance
      .recalculateInventoryStats()
      .catch((err) =>
        console.error('[InventoryLocalDB] Stats recalculation failed after upsertOne:', err)
      );
  }

  /** Hard-delete by product ID (used by incremental sync deletions). */
  export async function hardDelete(productId: string): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.hardDeleteInventoryItem(productId);
    notifyInventoryChanged();
    await instance
      .recalculateInventoryStats()
      .catch((err) =>
        console.error('[InventoryLocalDB] Stats recalculation failed after hardDelete:', err)
      );
  }

  /** Paginated read — primary data source for the Inventory V4 page and POS V3 products tab. */
  export async function getPage(params: {
    page: number;
    pageSize: number;
    search?: string;
    category?: string;
    taxable?: boolean;
    ebtEligible?: boolean;
    soldByWeight?: boolean;
    variablePrice?: boolean;
    productType?: ProductDataType['type'];
    /** Ordering for the returned page. Omit for the default alphabetical-by-name order. */
    sortBy?: 'recent' | 'name';
  }): Promise<{ items: ProductDataType[]; total: number }> {
    const instance = await PlatformService.getInstance();
    return instance.queryInventoryPage(params);
  }

  /** Count of active (non-deleted) products — for the stats row. */
  export async function count(): Promise<number> {
    const instance = await PlatformService.getInstance();
    return instance.countInventory();
  }

  /** Sum of stock levels across all active products — for the stats row. */
  export async function totalStock(productType?: ProductDataType['type']): Promise<number> {
    const instance = await PlatformService.getInstance();
    return instance.sumInventoryStock(productType);
  }

  /** Fetch a single active product by its primary ID. Returns null if not found or deleted. */
  export async function getById(productId: string): Promise<ProductDataType | null> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryById(productId);
  }

  /** Fetch a single active product by barcode/identifier. Returns null if not found or deleted. */
  export async function getByIdentifier(identifier: string): Promise<ProductDataType | null> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryByIdentifier(identifier);
  }

  /** Fetch a variable-price product by its short lookup code stored in physicalGood.variablePriceLookupCode. */
  export async function getByVariablePriceLookupCode(
    lookupCode: string
  ): Promise<ProductDataType | null> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryByVariablePriceLookupCode(lookupCode);
  }

  /** Wipe all inventory rows (called on logout or after migration confirmation). */
  export async function clearAll(): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.clearAllInventory();
    notifyInventoryChanged();
    await instance
      .recalculateInventoryStats()
      .catch((err) =>
        console.error('[InventoryLocalDB] Stats recalculation failed after clearAll:', err)
      );
  }

  /** Returns cached inventory stats, recalculating from source if the cache is missing. */
  export async function getStats(options?: {
    recalculateIfMissing?: boolean;
    forceRecalculate?: boolean;
  }): Promise<InventoryStats> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryStats(options);
  }

  /** Returns current per-category product counts directly from the inventory table. */
  export async function getCategoryCounts(
    productType?: ProductDataType['type']
  ): Promise<Array<{ category: string; count: number }>> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryCategoryCounts(productType);
  }

  /** Recalculates all inventory stats from source and persists the result to the cache table. */
  export async function recalculateStats(): Promise<InventoryStats> {
    const instance = await PlatformService.getInstance();
    return instance.recalculateInventoryStats();
  }

  /**
   * Whether a Firestore sync should be held off because migration is in flight.
   * The sync service checks this before writing to the inventory table.
   */
  export function isMigrationInProgress(): boolean {
    return isMigrating;
  }

  /**
   * One-time migration from old chunked system_config storage to the new inventory table.
   * Returns true if migration was performed, false if already done or no data to migrate.
   * Safe to call on every boot — guarded by a persistent sentinel key.
   */
  export async function migrateFromChunks(): Promise<boolean> {
    const instance = await PlatformService.getInstance();

    // Already migrated?
    const flag = await instance.getSystemConfig<{ done: boolean }>(MIGRATION_SENTINEL_KEY);
    if (flag?.data?.done === true) return false;

    isMigrating = true;
    try {
      // Ensure the inventory table exists before writing
      await instance.ensureInventoryTable();

      // Attempt to read old chunks
      const cached = await getInventoryFromLocalDb();
      if (!cached || cached.length === 0) {
        // No chunk data — first install or already cleared; mark done and exit
        await instance.updateOrCreateSystemConfig(MIGRATION_SENTINEL_KEY, { done: true });
        return false;
      }

      // Bulk-write to the new inventory table
      await instance.bulkUpsertInventory(cached);
      notifyInventoryChanged();

      // Persist sentinel so subsequent boots skip this
      await instance.updateOrCreateSystemConfig(MIGRATION_SENTINEL_KEY, { done: true });

      console.log(
        `[InventoryLocalDB] Migrated ${cached.length} products from chunks to inventory table`
      );

      // NOTE: Old chunks are intentionally left intact here.
      // They will be cleaned up in Phase 6 once the new path is confirmed stable.

      return true;
    } finally {
      isMigrating = false;
    }
  }
}
